import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useConfirm } from '../widget-ui/confirm-dialog';
import { Link, useSearchParams } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { profileRoleLabel } from '../widget-lib/role-label';

import { MobileLegalLinks } from '../widget-ui/mobile-legal-links';

import { Button } from '@domcrm/embed-ui';

import { listMyMinors, createMyMinor, deleteMyMinor, patchMyMinor, type RepresentedMinorItem } from '../widget-api/auth-minors';
import { patchMeProfile, postChangeMePassword, postRequestEmailVerification, type MeMembershipEntry } from '../widget-api/auth-me';
import { ApiError } from '../widget-api/http-client';
import { fetchMyPaymentsPage, MY_PAYMENTS_PAGE_SIZE, type MyPaymentItem } from '../widget-api/my-payments';
import { uploadMediaWithToken } from '../widget-api/media-upload';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { clearEmbedSession, clearGlobalEmbedSession } from '../widget-lib/embed-session-store';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { displayableMediaUrl } from '../widget-lib/media-url';
import { mergeWidgetQueryString } from '../widget-lib/merge-widget-query';
import { PATH } from '../widget-lib/path-routes';
import { validateNewPassword } from '../widget-lib/validation';
import { generateEmbedPassword } from '../widget-lib/generate-embed-password';
import { formatPhoneInputRu } from '../widget-lib/phone';
import { ImageCropModal } from '../widget-ui/image-crop-modal';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';
import { TelegramLinkCard } from '../widget-ui/telegram-link-card';
import { EmbedInput } from '../widget-ui/embed-input';
import { LogoutConfirmDialog } from '../widget-ui/logout-confirm-dialog';
import { DeleteAccountDialog } from '../widget-ui/delete-account-dialog';
import { LeaveOrganizationDialog } from '../widget-ui/leave-organization-dialog';
import { useSmartCaptcha } from '../widget-lib/smartcaptcha';
import { SmartCaptchaNotice } from '../widget-ui/smart-captcha-notice';

function membershipRoleLabel(role: string | null | undefined): string {
  const r = (role || '').trim().toLowerCase();
  if (!r) return 'Участник';
  if (r === 'client') return 'Клиент';
  return profileRoleLabel(r) ?? 'Участник';
}

function IconEye() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconEyeOff() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  );
}

function IconPencil() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

function MobileHubLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3.5 text-sm font-medium text-[var(--text)] transition-all duration-200 last:border-b-0 active:scale-[0.99] hover:bg-[var(--bg-soft)]"
    >
      {label}
      <span className="text-[var(--text-muted)] leading-none">›</span>
    </Link>
  );
}

function paymentStatusBadge(status: string): { label: string; className: string } {
  if (status === 'succeeded')
    return { label: 'Успешно', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200' };
  if (status === 'pending') return { label: 'Ожидание', className: 'bg-[var(--warn-bg)] text-[var(--warn-text)]' };
  if (status === 'failed') return { label: 'Ошибка', className: 'bg-[var(--error-bg)] text-[var(--error-text)]' };
  if (status === 'canceled' || status === 'refunded')
    return { label: status === 'refunded' ? 'Возврат' : 'Отменён', className: 'bg-[var(--bg-soft)] text-[var(--text-muted)]' };
  return { label: 'Статус уточняется', className: 'bg-[var(--bg-soft)] text-[var(--text-muted)]' };
}

function paymentTitle(p: MyPaymentItem): string {
  if (p.reference_type === 'user_subscription' || (p.reference_type ?? '').includes('subscription')) {
    return 'Оплата абонемента';
  }
  if (p.booking_id != null || p.reference_type === 'booking') {
    return 'Оплата записи';
  }
  return 'Платёж';
}

function groupPaymentsByMonth(items: MyPaymentItem[]): Map<string, MyPaymentItem[]> {
  const map = new Map<string, MyPaymentItem[]>();
  for (const p of items) {
    const d = p.created_at.slice(0, 7);
    const list = map.get(d) ?? [];
    list.push(p);
    map.set(d, list);
  }
  return map;
}

function formatRuShortDate(value: string | null | undefined): string {
  const raw = value?.trim();
  if (!raw) return '—';
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return raw;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

function AccountPaymentsMobile({ accessToken, backTo }: { accessToken: string; backTo: string }) {
  const [data, setData] = useState<MyPaymentItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moreAvailable, setMoreAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setData(null);
      setMoreAvailable(false);
      try {
        const page = await fetchMyPaymentsPage(accessToken);
        if (cancelled) return;
        setData(page.items);
        setTotal(page.total);
        setMoreAvailable(
          page.items.length === MY_PAYMENTS_PAGE_SIZE && page.total > page.items.length
        );
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Ошибка загрузки');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const loadMore = useCallback(async () => {
    if (!data?.length) return;
    const before = data[data.length - 1]!.id;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await fetchMyPaymentsPage(accessToken, { before_id: before });
      setData((prev) => {
        if (!prev) return page.items;
        const next = [...prev, ...page.items];
        setMoreAvailable(
          page.items.length === MY_PAYMENTS_PAGE_SIZE && page.total > next.length
        );
        return next;
      });
      setTotal(page.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
    } finally {
      setLoadingMore(false);
    }
  }, [accessToken, data]);

  const grouped = useMemo(() => groupPaymentsByMonth(data ?? []), [data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Link
          to={backTo}
          className="inline-flex min-h-9 items-center rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-3 text-sm font-medium text-[var(--text)] shadow-[var(--shadow-card)] transition-all duration-200 active:scale-[0.97]"
        >
          ← Кабинет
        </Link>
      </div>
      <h1 className="text-2xl font-bold tracking-tight text-[var(--text)]">История платежей</h1>
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-2xl bg-[var(--bg-soft)]" />
          ))}
        </div>
      ) : error ? (
        <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
          {error}
        </EmbedMessageBlock>
      ) : !data?.length ? (
        <p className="text-sm text-[var(--text-muted)]">Платежей пока нет.</p>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-[var(--text-muted)]">
            Показано {data.length} из {total}
          </p>
          <div className="space-y-6">
            {[...grouped.entries()]
              .sort((a, b) => b[0].localeCompare(a[0]))
              .map(([ym, rows]) => {
                let title = ym;
                try {
                  const parts = ym.split('-').map(Number);
                  const y = parts[0];
                  const m = parts[1];
                  if (y != null && m != null && Number.isFinite(y) && Number.isFinite(m)) {
                    title = new Date(y, m - 1, 1).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
                  }
                } catch {
                  title = ym;
                }
                return (
                  <section key={ym} className="space-y-3">
                    <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">{title}</h2>
                    <ul className="flex flex-col gap-3">
                      {rows.map((p) => {
                        const badge = paymentStatusBadge(p.status);
                        const when = (() => {
                          try {
                            return new Date(p.created_at).toLocaleDateString('ru-RU', {
                              day: 'numeric',
                              month: 'short'
                            });
                          } catch {
                            return p.created_at.slice(0, 10);
                          }
                        })();
                        const amt = Number.parseFloat(p.amount);
                        const amtStr = Number.isFinite(amt)
                          ? `${amt.toLocaleString('ru-RU')} ${p.currency === 'RUB' ? '₽' : p.currency}`
                          : `${p.amount} ${p.currency}`;
                        return (
                          <li
                            key={p.id}
                            className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)]"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-[var(--text)]">{paymentTitle(p)}</p>
                                <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                                  {p.booking_id != null ? `Запись #${p.booking_id}` : 'Абонемент'} · {when}
                                </p>
                              </div>
                              <div className="shrink-0 text-right">
                                <p className="text-base font-bold tabular-nums text-[var(--text)]">{amtStr}</p>
                                <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge.className}`}>
                                  {badge.label}
                                </span>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
          </div>
          {moreAvailable && (
            <div className="pt-1">
              <Button type="button" className="w-full" onClick={() => void loadMore()} disabled={loadingMore} variant="secondary">
                {loadingMore ? 'Загрузка…' : 'Показать ещё'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const pwdFieldBase =
  'min-h-10 w-full rounded-sm border-0 bg-[var(--bg-soft)] py-2 pl-3 text-sm text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]';
const pwdInputClass = `${pwdFieldBase} pr-11`;
const pwdInputNewClass = `${pwdFieldBase} pr-20`;

export default function AccountPage() {
  const confirmAction = useConfirm();
  useWidgetSeo(
    'Личный кабинет',
    'Персональный профиль, личные данные, редактирование профиля и управление аккаунтом.'
  );
  const suffix = useQuerySuffix();
  const [searchParams] = useSearchParams();
  const { accessToken, me, meLoading, meError, reloadMe, orgSlug, widgetContour, branchId, guestShowcaseOrg, effectiveModules } =
    useEmbedBootstrap();
  const modules = effectiveModules ?? me?.modules ?? null;
  const embedGlobal = searchParams.get('embed_global') === '1';
  const isMobile = widgetContour === 'mobile';
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNote, setResendNote] = useState<string | null>(null);
  const verifyOk = searchParams.get('email_verified') === '1';
  const verifyErr = searchParams.get('email_verify_error') === '1';

  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwNew2, setPwNew2] = useState('');
  const [showPwCur, setShowPwCur] = useState(false);
  const [showPwNew, setShowPwNew] = useState(false);
  const [showPwNew2, setShowPwNew2] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwErr, setPwErr] = useState<string | null>(null);

  // Yandex SmartCaptcha (невидимая): включается бэкендом (SMARTCAPTCHA_MODE=required).
  const captcha = useSmartCaptcha();

  const [dobOpen, setDobOpen] = useState(false);
  const [dobDraft, setDobDraft] = useState('');
  const [dobSaving, setDobSaving] = useState(false);
  const [dobErr, setDobErr] = useState<string | null>(null);

  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarErr, setAvatarErr] = useState<string | null>(null);
  const [childrenList, setChildrenList] = useState<RepresentedMinorItem[]>([]);
  const [minorModal, setMinorModal] = useState<'create' | 'edit' | null>(null);
  const [minorEditId, setMinorEditId] = useState<number | null>(null);
  const [minorName, setMinorName] = useState('');
  const [minorDob, setMinorDob] = useState('');
  const [minorConsent, setMinorConsent] = useState(false);
  const [minorBusy, setMinorBusy] = useState(false);
  const [minorErr, setMinorErr] = useState<string | null>(null);
  const [parentToggleBusy, setParentToggleBusy] = useState(false);

  const [nameOpen, setNameOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameErr, setNameErr] = useState<string | null>(null);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [leaveTarget, setLeaveTarget] = useState<MeMembershipEntry | null>(null);
  const avatarFileInputId = useId();
  const pwCurId = useId();
  const pwNewId = useId();
  const pwNew2Id = useId();
  const nameFieldId = useId();
  const emailFieldId = useId();

  useEffect(() => {
    if (!accessToken) {
      setChildrenList([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await listMyMinors(accessToken);
        if (cancelled) return;
        setChildrenList(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setChildrenList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  useEffect(() => {
    if (me?.date_of_birth) setDobDraft(me.date_of_birth.trim());
    else setDobDraft('');
  }, [me?.date_of_birth]);

  function openDobDialog(): void {
    setDobDraft(me?.date_of_birth?.trim() ?? '');
    setDobErr(null);
    setDobOpen(true);
  }

  function openPwDialog(): void {
    setPwCurrent('');
    setPwNew('');
    setPwNew2('');
    setPwErr(null);
    setShowPwCur(false);
    setShowPwNew(false);
    setShowPwNew2(false);
    setPwOpen(true);
  }

  function openMinorCreate(): void {
    setMinorModal('create');
    setMinorEditId(null);
    setMinorName('');
    setMinorDob('');
    setMinorConsent(false);
    setMinorErr(null);
  }

  function openMinorEdit(c: RepresentedMinorItem): void {
    setMinorModal('edit');
    setMinorEditId(c.id);
    setMinorName((c.name ?? '').trim());
    setMinorDob((c.date_of_birth ?? '').trim());
    setMinorConsent(true);
    setMinorErr(null);
  }

  function closeMinorModal(): void {
    if (minorBusy) return;
    setMinorModal(null);
    setMinorEditId(null);
  }

  function openNameDialog(): void {
    setNameDraft(me?.full_name?.trim() ?? '');
    setNameErr(null);
    setNameOpen(true);
  }

  function openEmailDialog(): void {
    setEmailDraft(me?.email?.trim() ?? '');
    setEmailErr(null);
    setEmailOpen(true);
  }

  function onAvatarFile(ev: React.ChangeEvent<HTMLInputElement>): void {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setCropOpen(true);
  }

  function closeCrop(): void {
    setCropOpen(false);
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }

  const accountTab = searchParams.get('account_tab');
  const paymentsQs = mergeWidgetQueryString(searchParams.toString(), { account_tab: 'payments' });
  const accountBaseQs = mergeWidgetQueryString(searchParams.toString(), { account_tab: null });

  const handleAccountDeleted = useCallback(() => {
    clearEmbedSession(orgSlug);
    clearGlobalEmbedSession();
    const params = new URLSearchParams(suffix.startsWith('?') ? suffix.slice(1) : suffix);
    params.set('account_deleted', '1');
    if (widgetContour === 'trainer' || widgetContour === 'trainer-mobile') {
      window.location.href = `/embed/trainer/sign-in?${params.toString()}`;
      return;
    }
    if (embedGlobal) {
      const p = new URLSearchParams();
      p.set('embed_global', '1');
      p.set('account_deleted', '1');
      const parentOriginVal = searchParams.get('embed_parent_origin');
      if (parentOriginVal) p.set('embed_parent_origin', parentOriginVal);
      const wnVal = searchParams.get('wn');
      if (wnVal) p.set('wn', wnVal);
      const widgetChannelVal = searchParams.get('widget_channel');
      if (widgetChannelVal) p.set('widget_channel', widgetChannelVal);
      window.location.href = `/embed/?${p.toString()}`;
      return;
    }
    window.location.href = `/embed/sign-in?${params.toString()}`;
  }, [orgSlug, suffix, widgetContour, embedGlobal, searchParams]);

  if (!accessToken) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Личный кабинет</h1>
        <p className="text-sm text-[var(--text-muted)]">Войдите, чтобы видеть профиль и записи.</p>
        <Link
          className="inline-block text-sm font-medium text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
          to={`${PATH.signIn}${suffix}`}
        >
          Вход
        </Link>
      </div>
    );
  }

  if (isMobile && accountTab === 'payments') {
    return <AccountPaymentsMobile accessToken={accessToken} backTo={`${PATH.account}${accountBaseQs}`} />;
  }

  if (meLoading) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <div className="h-7 w-36 animate-pulse rounded bg-[var(--bg-soft)]" />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="h-28 w-28 animate-pulse rounded-full bg-[var(--bg-soft)]" />
          <div className="grid flex-1 gap-3 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <div className="h-3 w-16 animate-pulse rounded bg-[var(--bg-soft)]" />
                <div className="h-4 w-32 animate-pulse rounded bg-[var(--bg-soft)]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (meError) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Личный кабинет</h1>
        <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
          {meError}
        </EmbedMessageBlock>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="w-full min-w-0">
        <p className="text-sm text-[var(--text-muted)]">Нет данных профиля.</p>
      </div>
    );
  }

  const emailNeedsVerify = !me.email_verified_at;
  const isClientRole = (me.role || '').toLowerCase() === 'client';
  const avatarUrl = displayableMediaUrl(me.avatar_url);
  const showAvatarImg = avatarUrl != null;
  const initial = (me.full_name?.trim() || me.email || '?')[0]?.toUpperCase() ?? '?';

  return (
    <div className="space-y-4">
      {isMobile ? (
        <>
          <section className="overflow-hidden rounded-2xl bg-gradient-to-br from-[color-mix(in_srgb,var(--accent)_12%,transparent)] to-[var(--bg-elevated)] p-5 shadow-[var(--shadow-card)] backdrop-blur-md">
            <div className="flex flex-col items-center text-center">
              <div className="flex h-[72px] w-[72px] items-center justify-center overflow-hidden rounded-full bg-[var(--bg-soft)] text-2xl font-bold text-[var(--accent)] ring-2 ring-[color-mix(in_srgb,var(--accent)_25%,transparent)]">
                {showAvatarImg ? (
                  <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span aria-hidden>{initial}</span>
                )}
              </div>
              <h1 className="mt-3 text-xl font-bold tracking-tight text-[var(--text)]">
                {me.full_name?.trim() || 'Профиль'}
              </h1>
              <p className="mt-1 text-sm text-[var(--text-muted)]">{me.email}</p>
              <Button type="button" variant="ghost" className="mt-3" onClick={() => document.getElementById('embed-account-fields')?.scrollIntoView({ behavior: 'smooth' })}>
                Редактировать профиль
              </Button>
            </div>
          </section>

          <nav className="overflow-hidden rounded-2xl bg-[var(--bg-elevated)] shadow-[var(--shadow-card)]">
            {(!embedGlobal || orgSlug || guestShowcaseOrg) && (
              <MobileHubLink to={`${PATH.schedule}${suffix}`} label="Расписание" />
            )}
            <MobileHubLink to={`${PATH.bookings}${suffix}`} label="Мои записи" />
            {modules?.qrcode === true && (
              <MobileHubLink to={`${PATH.myAccess}${suffix}`} label="QR-пропуск" />
            )}
            <MobileHubLink to={`${PATH.mySubscriptions}${suffix}`} label="Мои абонементы" />
            <MobileHubLink to={`${PATH.account}${paymentsQs}`} label="История платежей" />
            {isClientRole ? (
              <MobileHubLink to={`${PATH.account}${suffix}#children`} label={me.is_parent_for_minors || childrenList.length > 0 ? 'Мои дети' : 'Подопечные'} />
            ) : null}
            <Link
              to={`${PATH.account}${suffix}#embed-account-notifications`}
              className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3.5 text-sm font-medium text-[var(--text)] transition-all duration-200 active:scale-[0.99] hover:bg-[var(--bg-soft)]"
            >
              Уведомления
              <span className="text-[var(--text-muted)] leading-none">›</span>
            </Link>
            <button
              type="button"
              className="flex w-full items-center justify-between px-4 py-3.5 text-left text-sm font-medium text-[var(--text)] transition-all duration-200 active:scale-[0.99] hover:bg-[var(--bg-soft)]"
              onClick={openPwDialog}
            >
              Сменить пароль
              <span className="text-[var(--text-muted)] leading-none">›</span>
            </button>
          </nav>

          <MobileLegalLinks />

          {isClientRole && !me.is_parent_for_minors && childrenList.length === 0 ? (
            <section className="space-y-3 rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)]">
              <h2 className="text-sm font-semibold text-[var(--text)]">Подопечные</h2>
              <p className="text-xs leading-relaxed text-[var(--text-muted)]">
                Включите режим родителя, чтобы добавлять детей и записывать их на занятия.
              </p>
              <Button
                type="button"
                className="w-full"
                disabled={parentToggleBusy}
                onClick={async () => {
                  if (!accessToken) return;
                  setParentToggleBusy(true);
                  try {
                    await patchMeProfile({ is_parent_for_minors: true }, accessToken);
                    await reloadMe();
                  } catch (e) {
                    const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось сохранить';
                    window.alert(msg);
                  } finally {
                    setParentToggleBusy(false);
                  }
                }}
              >
                {parentToggleBusy ? 'Сохраняем…' : 'Включить режим родителя'}
              </Button>
            </section>
          ) : null}

          {isClientRole && (me.is_parent_for_minors || childrenList.length > 0) ? (
            <section id="children" className="space-y-3 rounded-2xl bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)]">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Дети</h2>
              <ul className="flex flex-col gap-2">
                {childrenList.map((c) => {
                  const label = (c.name ?? '').trim() || `Ребёнок #${c.id}`;
                  return (
                    <li
                      key={c.id}
                      className="flex flex-col gap-2 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--bg-elevated)] text-xs font-bold text-[var(--accent)]">
                          {label[0]?.toUpperCase() ?? '?'}
                        </span>
                        <span className="truncate text-sm font-medium text-[var(--text)]">{label}</span>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <Link
                          to={`${PATH.bookings}${mergeWidgetQueryString(searchParams.toString(), { minor_id: String(c.id) })}`}
                          className="text-xs font-medium text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                        >
                          Записи
                        </Link>
                        <button
                          type="button"
                          className="text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text)]"
                          onClick={() => openMinorEdit(c)}
                        >
                          Изменить
                        </button>
                        <button
                          type="button"
                          className="text-xs font-medium text-[var(--error-text)] hover:underline"
                          onClick={async () => {
                            if (!accessToken) return;
                            if (!(await confirmAction({ title: 'Удалить из списка?', message: label, confirmLabel: 'Удалить', danger: true }))) return;
                            try {
                              await deleteMyMinor(accessToken, c.id);
                              await listMyMinors(accessToken).then(setChildrenList);
                            } catch (e) {
                              const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось удалить';
                              window.alert(msg);
                            }
                          }}
                        >
                          Удалить
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-col gap-2">
                <Button type="button" variant="secondary" className="w-full" onClick={openMinorCreate}>
                  + Добавить ребёнка
                </Button>
                {childrenList.length === 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full text-xs text-[var(--text-muted)] hover:text-[var(--text)]"
                    disabled={parentToggleBusy}
                    onClick={async () => {
                      if (!accessToken) return;
                      setParentToggleBusy(true);
                      try {
                        await patchMeProfile({ is_parent_for_minors: false }, accessToken);
                        await reloadMe();
                      } catch (e) {
                        const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось сохранить';
                        window.alert(msg);
                      } finally {
                        setParentToggleBusy(false);
                      }
                    }}
                  >
                    {parentToggleBusy ? 'Сохраняем…' : 'Выйти из режима родителя'}
                  </Button>
                )}
              </div>
            </section>
          ) : null}

          <div className="rounded-2xl border-2 border-red-300 bg-red-50/50 p-1 shadow-[var(--shadow-card)] dark:border-red-800 dark:bg-red-950/20">
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-xl px-4 py-3.5 text-sm font-semibold text-red-600 transition-all duration-200 active:scale-[0.99] hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-950/40"
              onClick={() => setLogoutOpen(true)}
            >
              Выйти
              <span className="text-base leading-none opacity-60">→</span>
            </button>
          </div>
        </>
      ) : null}
    <div id="embed-account-fields" className="space-y-4 w-full min-w-0">
      <h1 className={`${isMobile ? 'text-lg font-bold tracking-tight' : 'text-xl font-semibold tracking-tight'}`}>
        {isMobile ? 'Данные профиля' : 'Личный кабинет'}
      </h1>
      {verifyOk && (
        <p className="rounded-sm border border-[color-mix(in_srgb,var(--border)_70%,transparent)] bg-[var(--bg-soft)] px-3 py-2 text-sm text-[var(--text)]">
          Электронная почта подтверждена.
        </p>
      )}
      {verifyErr && (
        <p className="rounded-sm bg-[var(--warn-bg)] px-3 py-2 text-sm text-[var(--warn-text)]">
          Не удалось подтвердить адрес по ссылке. Запросите письмо ещё раз или обратитесь в студию.
        </p>
      )}
      {!isMobile ? (
        <div className="flex flex-wrap gap-2 border-b border-[color-mix(in_srgb,var(--border)_50%,transparent)] pb-3">
          {(!embedGlobal || orgSlug || guestShowcaseOrg) && (
            <Link
              to={`${PATH.schedule}${suffix}`}
              className="inline-flex min-h-9 items-center rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] px-3 text-sm font-medium text-[var(--text)] transition hover:bg-[var(--bg-soft)]"
            >
              Расписание
            </Link>
          )}
          <Link
            to={`${PATH.bookings}${suffix}`}
            className="inline-flex min-h-9 items-center rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] px-3 text-sm font-medium text-[var(--text)] transition hover:bg-[var(--bg-soft)]"
          >
            Мои записи
          </Link>
          <Link
            to={`${PATH.mySubscriptions}${suffix}`}
            className="inline-flex min-h-9 items-center rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] px-3 text-sm font-medium text-[var(--text)] transition hover:bg-[var(--bg-soft)]"
          >
            Мои абонементы
          </Link>
          <Link
            to={`${PATH.billing}${suffix}`}
            className="inline-flex min-h-9 items-center rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] px-3 text-sm font-medium text-[var(--text)] transition hover:bg-[var(--bg-soft)]"
          >
            История платежей
          </Link>
        </div>
      ) : null}
      {emailNeedsVerify && (
        <div className="rounded-sm bg-[var(--warn-bg)] px-3 py-2 text-sm text-[var(--text)]">
          <p className="text-[var(--warn-text)]">
            Подтвердите email — перейдите по ссылке из письма. Не пришло письмо?
          </p>
          <button
            type="button"
            disabled={resendBusy}
            className="mt-2 text-sm font-medium text-[var(--accent)] underline-offset-2 hover:underline disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            onClick={async () => {
              if (!accessToken) return;
              setResendBusy(true);
              setResendNote(null);
              try {
                await postRequestEmailVerification(accessToken);
                setResendNote('Если адрес зарегистрирован, мы отправили письмо. Проверьте папку «Спам».');
              } catch (e) {
                const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось отправить';
                setResendNote(msg);
              } finally {
                setResendBusy(false);
                reloadMe();
              }
            }}
          >
            {resendBusy ? 'Отправляем…' : 'Отправить письмо ещё раз'}
          </button>
          {resendNote && <p className="mt-2 text-xs text-[var(--text-muted)]">{resendNote}</p>}
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="flex shrink-0 flex-col items-center gap-2">
          <label
            htmlFor={avatarFileInputId}
            className="relative flex h-[min(7rem,28vw)] w-[min(7rem,28vw)] max-h-32 max-w-32 min-h-[3rem] min-w-[3rem] shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-[var(--bg-soft)] text-2xl font-semibold text-[var(--text-muted)] ring-offset-2 focus-within:outline-none focus-within:ring-2 focus-within:ring-[var(--focus-ring)] active:opacity-90"
            aria-label="Загрузить или сменить фото профиля"
          >
            <input
              id={avatarFileInputId}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={onAvatarFile}
              disabled={avatarBusy}
            />
            {showAvatarImg ? (
              <img src={avatarUrl} alt="" className="pointer-events-none h-full w-full object-cover" />
            ) : (
              <span aria-hidden className="pointer-events-none">
                {initial}
              </span>
            )}
            {avatarBusy ? (
              <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 text-xs font-medium text-white">
                …
              </span>
            ) : null}
          </label>
          {showAvatarImg ? (
            <Button
              type="button"
              variant="ghost"
              className="text-xs"
              disabled={avatarBusy}
              onClick={async () => {
                if (!accessToken) return;
                setAvatarErr(null);
                setAvatarBusy(true);
                try {
                  await patchMeProfile({ avatar_url: null }, accessToken);
                  await reloadMe();
                } catch (e) {
                  const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось удалить';
                  setAvatarErr(msg);
                } finally {
                  setAvatarBusy(false);
                }
              }}
            >
              Удалить фото
            </Button>
          ) : null}
          {avatarErr ? (
            <EmbedMessageBlock
              variant="error"
              size="field"
              className="w-full max-w-[12rem] [&>p]:text-center"
              role="alert"
              aria-live="assertive"
            >
              {avatarErr}
            </EmbedMessageBlock>
          ) : null}
        </div>

        <dl className="grid min-w-0 flex-1 gap-3 text-sm sm:grid-cols-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <dt className="text-[13px] text-[var(--text-muted)]">ФИО</dt>
              <dd className="mt-0.5 text-[var(--text)]">{me.full_name?.trim() || '—'}</dd>
            </div>
            <button
              type="button"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
              onClick={openNameDialog}
              aria-label="Изменить ФИО"
            >
              <IconPencil />
            </button>
          </div>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <dt className="text-[13px] text-[var(--text-muted)]">Email</dt>
              <dd className="mt-0.5 break-all text-[var(--text)]">{me.email || '—'}</dd>
            </div>
            {isClientRole ? (
              <button
                type="button"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                onClick={openEmailDialog}
                aria-label="Изменить email"
              >
                <IconPencil />
              </button>
            ) : null}
          </div>
          <div className="sm:col-span-2">
            <dt className="text-[13px] text-[var(--text-muted)]">Телефон</dt>
            <dd className="mt-0.5 font-mono text-sm text-[var(--text)]">
              {me.phone && String(me.phone).trim() ? formatPhoneInputRu(String(me.phone).trim()) : '—'}
            </dd>
            <p className="mt-1 text-[11px] leading-snug text-[var(--text-muted)]">
              Номер используется для входа. Чтобы сменить его, обратитесь в студию.
            </p>
          </div>
          {profileRoleLabel(me.role) ? (
            <div>
              <dt className="text-[13px] text-[var(--text-muted)]">Роль</dt>
              <dd className="mt-0.5 text-[var(--text)]">{profileRoleLabel(me.role)}</dd>
            </div>
          ) : null}
          <div className="flex flex-wrap items-start justify-between gap-2 sm:col-span-2">
            <div className="min-w-0">
              <dt className="text-[13px] text-[var(--text-muted)]">Дата рождения</dt>
              <dd className="mt-0.5 text-[var(--text)]">{formatRuShortDate(me.date_of_birth)}</dd>
            </div>
            <button
              type="button"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
              onClick={openDobDialog}
              aria-label="Изменить дату рождения"
            >
              <IconPencil />
            </button>
          </div>
        </dl>
      </div>

      {!isMobile && isClientRole && !me.is_parent_for_minors && childrenList.length === 0 ? (
        <section className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)]">
          <h2 className="text-sm font-semibold text-[var(--text)]">Подопечные</h2>
          <p className="mt-1 text-xs leading-relaxed text-[var(--text-muted)]">
            Включите режим родителя, чтобы добавлять детей и записывать их на занятия.
          </p>
          <Button
            type="button"
            className="mt-3"
            disabled={parentToggleBusy}
            onClick={async () => {
              if (!accessToken) return;
              setParentToggleBusy(true);
              try {
                await patchMeProfile({ is_parent_for_minors: true }, accessToken);
                await reloadMe();
              } catch (e) {
                const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось сохранить';
                window.alert(msg);
              } finally {
                setParentToggleBusy(false);
              }
            }}
          >
            {parentToggleBusy ? 'Сохраняем…' : 'Включить режим родителя'}
          </Button>
        </section>
      ) : null}

      {!isMobile && isClientRole && (me.is_parent_for_minors || childrenList.length > 0) ? (
        <section id="children-desktop" className="space-y-3 rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)]">
          <h2 className="text-sm font-semibold text-[var(--text)]">Дети</h2>
          <ul className="flex flex-col gap-2">
            {childrenList.map((c) => {
              const label = (c.name ?? '').trim() || `Ребёнок #${c.id}`;
              return (
                <li
                  key={c.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--bg-elevated)] text-xs font-bold text-[var(--accent)]">
                      {label[0]?.toUpperCase() ?? '?'}
                    </span>
                    <span className="truncate text-sm font-medium text-[var(--text)]">{label}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`${PATH.bookings}${mergeWidgetQueryString(searchParams.toString(), { minor_id: String(c.id) })}`}
                      className="text-xs font-medium text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                    >
                      Записи
                    </Link>
                    <button type="button" className="text-xs text-[var(--text-muted)] hover:text-[var(--text)]" onClick={() => openMinorEdit(c)}>
                      Изменить
                    </button>
                    <button
                      type="button"
                      className="text-xs text-[var(--error-text)] hover:underline"
                      onClick={async () => {
                        if (!accessToken) return;
                        if (!(await confirmAction({ title: 'Удалить из списка?', message: label, confirmLabel: 'Удалить', danger: true }))) return;
                        try {
                          await deleteMyMinor(accessToken, c.id);
                          const rows = await listMyMinors(accessToken);
                          setChildrenList(Array.isArray(rows) ? rows : []);
                        } catch (e) {
                          const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось удалить';
                          window.alert(msg);
                        }
                      }}
                    >
                      Удалить
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={openMinorCreate}>
              + Добавить ребёнка
            </Button>
            {childrenList.length === 0 && (
              <Button
                type="button"
                variant="secondary"
                disabled={parentToggleBusy}
                onClick={async () => {
                  if (!accessToken) return;
                  setParentToggleBusy(true);
                  try {
                    await patchMeProfile({ is_parent_for_minors: false }, accessToken);
                    await reloadMe();
                  } catch (e) {
                    const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось сохранить';
                    window.alert(msg);
                  } finally {
                    setParentToggleBusy(false);
                  }
                }}
              >
                {parentToggleBusy ? 'Сохраняем…' : 'Выйти из режима родителя'}
              </Button>
            )}
          </div>
        </section>
      ) : null}

      <section className="rounded-sm border border-[color-mix(in_srgb,var(--border)_70%,transparent)] bg-[var(--bg-soft)] p-4 text-sm">
        <h2 className="text-[13px] font-medium text-[var(--text)]">Смена пароля</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-[var(--text-muted)]">
          Укажите текущий пароль и новый. Если забыли пароль — воспользуйтесь ссылкой «Забыли пароль» на странице входа.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button type="button" onClick={openPwDialog}>
            Сменить пароль
          </Button>
          <Link
            className="inline-flex h-9 items-center text-[13px] font-medium text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            to={`${PATH.forgotPassword}${suffix}`}
          >
            Забыли пароль
          </Link>
        </div>
      </section>

      <div id="embed-account-notifications">
        <TelegramLinkCard
          accessToken={accessToken}
          branchId={branchId}
          telegramId={me.telegram_id}
          notifyViaTelegram={me.notify_via_telegram}
          onReload={() => reloadMe()}
          title="Telegram-уведомления"
        />
      </div>

      {cropSrc && cropOpen ? (
        <ImageCropModal
          open
          imageSrc={cropSrc}
          onOpenChange={(o) => {
            if (!o) closeCrop();
          }}
          title="Фото профиля"
          hint="Квадрат 1:1, как в админке. После обрезки фото сохранится в профиле (до 512×512 px для быстрой загрузки)."
          outputLimits={{ maxOutputWidth: 512, maxOutputHeight: 512 }}
          onApplyError={(m) => setAvatarErr(m)}
          onApply={async (file) => {
            if (!accessToken) return;
            setAvatarErr(null);
            setAvatarBusy(true);
            try {
              const res = await uploadMediaWithToken(file, accessToken, { imageProfile: 'square' });
              const url = res?.url;
              if (!url) return;
              await patchMeProfile({ avatar_url: url }, accessToken);
              await reloadMe();
            } catch (e) {
              const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Ошибка загрузки';
              setAvatarErr(msg);
              throw e;
            } finally {
              setAvatarBusy(false);
            }
          }}
        />
      ) : null}

      {dobOpen ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/55 p-4"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !dobSaving) setDobOpen(false);
          }}
        >
          <div
            className="w-full max-w-md rounded-sm bg-[var(--bg-elevated)] p-5 shadow-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dob-dialog-title"
          >
            <h2 id="dob-dialog-title" className="text-lg font-semibold text-[var(--text)]">
              Дата рождения
            </h2>
            <p className="mt-1 text-sm text-[var(--text-muted)]">Формат ГГГГ-ММ-ДД.</p>
            <input
              type="date"
              className="mt-3 min-h-10 w-full rounded-sm border-0 bg-[var(--bg-soft)] px-3 py-2 text-sm text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
              value={dobDraft}
              onChange={(e) => setDobDraft(e.target.value)}
            />
            {dobErr ? (
              <EmbedMessageBlock
                className="mt-2 w-full"
                variant="error"
                size="field"
                role="alert"
                aria-live="assertive"
              >
                {dobErr}
              </EmbedMessageBlock>
            ) : null}
            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="secondary" disabled={dobSaving} onClick={() => setDobOpen(false)}>
                Отмена
              </Button>
              <Button
                type="button"
                disabled={dobSaving}
                onClick={async () => {
                  if (!accessToken) return;
                  const v = dobDraft.trim();
                  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
                    setDobErr('Укажите дату в формате ГГГГ-ММ-ДД.');
                    return;
                  }
                  setDobErr(null);
                  setDobSaving(true);
                  try {
                    await patchMeProfile({ date_of_birth: v }, accessToken);
                    await reloadMe();
                    setDobOpen(false);
                  } catch (e) {
                    const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не сохранилось';
                    setDobErr(msg);
                  } finally {
                    setDobSaving(false);
                  }
                }}
              >
                {dobSaving ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {minorModal ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/55 p-4"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !minorBusy) closeMinorModal();
          }}
        >
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-sm bg-[var(--bg-elevated)] p-5 shadow-lg" role="dialog" aria-modal="true">
            <h2 className="text-lg font-semibold text-[var(--text)]">{minorModal === 'create' ? 'Новый ребёнок' : 'Редактирование'}</h2>
            <p className="mt-1 text-sm text-[var(--text-muted)]">
              Имя (одно или два слова) и дата рождения — этого достаточно для записи на детские занятия. Другие данные не собираются.
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-[13px] text-[var(--text-muted)]" htmlFor="minor-fn">
                  Имя
                </label>
                <EmbedInput id="minor-fn" className="mt-1" value={minorName} placeholder="Например: Иван или Иван Петров" onChange={(e) => setMinorName(e.target.value)} autoComplete="name" />
              </div>
              <div>
                <label className="text-[13px] text-[var(--text-muted)]" htmlFor="minor-dob">
                  Дата рождения
                </label>
                <input
                  id="minor-dob"
                  type="date"
                  className="mt-1 min-h-10 w-full rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--accent)] focus:outline-none"
                  value={minorDob}
                  onChange={(e) => setMinorDob(e.target.value)}
                />
              </div>
              {minorModal === 'create' ? (
                <label className="flex items-start gap-2 text-[13px] text-[var(--text-muted)]">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={minorConsent}
                    onChange={(e) => setMinorConsent(e.target.checked)}
                  />
                  <span>
                    Я, как законный представитель, даю согласие на обработку персональных данных ребёнка
                    (имя и дата рождения) для записи на занятия.
                  </span>
                </label>
              ) : null}
            </div>
            {minorErr ? (
              <EmbedMessageBlock className="mt-3 w-full" variant="error" size="field" role="alert">
                {minorErr}
              </EmbedMessageBlock>
            ) : null}
            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="secondary" disabled={minorBusy} onClick={closeMinorModal}>
                Отмена
              </Button>
              <Button
                type="button"
                disabled={minorBusy}
                onClick={async () => {
                  if (!accessToken) return;
                  const fn = minorName.trim();
                  const dob = minorDob.trim();
                  if (!fn) {
                    setMinorErr('Укажите имя ребёнка.');
                    return;
                  }
                  if (fn.split(/\s+/).length > 2) {
                    setMinorErr('Имя — одно или два слова, без фамилии и отчества.');
                    return;
                  }
                  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
                    setMinorErr('Укажите дату рождения.');
                    return;
                  }
                  if (minorModal === 'create' && !minorConsent) {
                    setMinorErr('Подтвердите согласие законного представителя.');
                    return;
                  }
                  setMinorErr(null);
                  setMinorBusy(true);
                  try {
                    if (minorModal === 'create') {
                      await createMyMinor(accessToken, {
                        name: fn,
                        date_of_birth: dob,
                        consent: minorConsent
                      });
                    } else if (minorEditId != null) {
                      await patchMyMinor(accessToken, minorEditId, {
                        name: fn,
                        date_of_birth: dob
                      });
                    }
                    const rows = await listMyMinors(accessToken);
                    setChildrenList(Array.isArray(rows) ? rows : []);
                    closeMinorModal();
                  } catch (e) {
                    const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось сохранить';
                    setMinorErr(msg);
                  } finally {
                    setMinorBusy(false);
                  }
                }}
              >
                {minorBusy ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {nameOpen ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/55 p-4"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !nameSaving) setNameOpen(false);
          }}
        >
          <div className="w-full max-w-md rounded-sm bg-[var(--bg-elevated)] p-5 shadow-lg" role="dialog" aria-modal="true">
            <h2 className="text-lg font-semibold text-[var(--text)]">ФИО</h2>
            <EmbedInput id={nameFieldId} className="mt-3" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} autoComplete="name" />
            {nameErr ? (
              <EmbedMessageBlock className="mt-2 w-full" variant="error" size="field" role="alert">
                {nameErr}
              </EmbedMessageBlock>
            ) : null}
            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="secondary" disabled={nameSaving} onClick={() => setNameOpen(false)}>
                Отмена
              </Button>
              <Button
                type="button"
                disabled={nameSaving}
                onClick={async () => {
                  if (!accessToken) return;
                  const v = nameDraft.trim();
                  if (!v) {
                    setNameErr('Укажите ФИО.');
                    return;
                  }
                  setNameErr(null);
                  setNameSaving(true);
                  try {
                    await patchMeProfile({ full_name: v }, accessToken);
                    await reloadMe();
                    setNameOpen(false);
                  } catch (e) {
                    const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не сохранилось';
                    setNameErr(msg);
                  } finally {
                    setNameSaving(false);
                  }
                }}
              >
                {nameSaving ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {emailOpen ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/55 p-4"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !emailSaving) setEmailOpen(false);
          }}
        >
          <div className="w-full max-w-md rounded-sm bg-[var(--bg-elevated)] p-5 shadow-lg" role="dialog" aria-modal="true">
            <h2 className="text-lg font-semibold text-[var(--text)]">Email</h2>
            <p className="mt-1 text-sm text-[var(--text-muted)]">После смены адреса может потребоваться подтверждение по ссылке из письма.</p>
            <input
              type="email"
              id={emailFieldId}
              className="mt-3 min-h-10 w-full rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--accent)] focus:outline-none"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              autoComplete="email"
            />
            {emailErr ? (
              <EmbedMessageBlock className="mt-2 w-full" variant="error" size="field" role="alert">
                {emailErr}
              </EmbedMessageBlock>
            ) : null}
            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="secondary" disabled={emailSaving} onClick={() => setEmailOpen(false)}>
                Отмена
              </Button>
              <Button
                type="button"
                disabled={emailSaving}
                onClick={async () => {
                  if (!accessToken) return;
                  const v = emailDraft.trim().toLowerCase();
                  if (!v) {
                    setEmailErr('Укажите email.');
                    return;
                  }
                  setEmailErr(null);
                  setEmailSaving(true);
                  try {
                    await patchMeProfile({ email: v }, accessToken);
                    await reloadMe();
                    setEmailOpen(false);
                  } catch (e) {
                    const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не сохранилось';
                    setEmailErr(msg);
                  } finally {
                    setEmailSaving(false);
                  }
                }}
              >
                {emailSaving ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {me && accessToken && (me.memberships ?? []).length > 0 ? (
        <section className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4">
          <h2 className="text-sm font-semibold text-[var(--text)]">Организации</h2>
          <ul className="mt-3 space-y-2">
            {(me.memberships ?? []).map((m) => (
              <li
                key={m.organization_id}
                className="flex items-center justify-between gap-3 rounded-xl border border-[var(--border)] px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm text-[var(--text)]">
                    {m.organization_name || m.organization_slug || 'Текущая организация'}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">{membershipRoleLabel(m.role)}</p>
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium text-[var(--error-text)] transition-colors hover:bg-[var(--error-bg)]"
                  onClick={() => setLeaveTarget(m)}
                >
                  Выйти
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {!isMobile ? (
        <div className="pt-2">
          <div className="inline-block rounded-xl border-2 border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20">
            <button
              type="button"
              className="flex w-full items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-950/40"
              onClick={() => setLogoutOpen(true)}
            >
              Выйти из аккаунта
              <span className="text-base leading-none opacity-60">→</span>
            </button>
          </div>
        </div>
      ) : null}

      {me && accessToken ? (
        <div className="pt-2">
          <div className="inline-block rounded-xl border-2 border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20">
            <button
              type="button"
              className="flex w-full items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-950/40"
              onClick={() => setDeleteOpen(true)}
            >
              Удалить аккаунт
            </button>
          </div>
          <p className="mt-2 max-w-md text-xs leading-relaxed text-[var(--text-muted)]">
            Профиль и данные удаляются безвозвратно; история бронирований у организации сохраняется в обезличенном виде.
          </p>
        </div>
      ) : null}

      {pwOpen ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/55 p-4"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !pwSaving) setPwOpen(false);
          }}
        >
          <div
            className="w-full max-w-md rounded-sm bg-[var(--bg-elevated)] p-5 shadow-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pw-dialog-title"
          >
            <h2 id="pw-dialog-title" className="text-lg font-semibold text-[var(--text)]">
              Смена пароля
            </h2>
            <p className="mt-1 text-sm text-[var(--text-muted)]">После сохранения потребуется войти снова с новым паролем.</p>

            <div className="mt-4 space-y-3">
              <div>
                <label htmlFor={pwCurId} className="text-[13px] text-[var(--text-muted)]">
                  Текущий пароль
                </label>
                <div className="relative mt-1">
                  <input
                    id={pwCurId}
                    type={showPwCur ? 'text' : 'password'}
                    autoComplete="current-password"
                    className={pwdInputClass}
                    value={pwCurrent}
                    onChange={(e) => setPwCurrent(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-sm text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text)]"
                    onClick={() => setShowPwCur((v) => !v)}
                    aria-label={showPwCur ? 'Скрыть пароль' : 'Показать пароль'}
                  >
                    {showPwCur ? <IconEyeOff /> : <IconEye />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor={pwNewId} className="text-[13px] text-[var(--text-muted)]">
                  Новый пароль
                </label>
                <div className="relative mt-1">
                  <input
                    id={pwNewId}
                    type={showPwNew ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={pwdInputNewClass}
                    value={pwNew}
                    onChange={(e) => setPwNew(e.target.value)}
                  />
                  <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center">
                    <button
                      type="button"
                      className="flex h-8 w-8 items-center justify-center rounded-sm text-[var(--accent)] hover:bg-[var(--bg-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                      onClick={() => {
                        setPwNew(generateEmbedPassword());
                        setPwNew2('');
                      }}
                      aria-label="Сгенерировать пароль"
                      title="Сгенерировать пароль"
                    >
                      <Sparkles className="h-4 w-4" strokeWidth={2} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="flex h-8 w-8 items-center justify-center rounded-sm text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text)]"
                      onClick={() => setShowPwNew((v) => !v)}
                      aria-label={showPwNew ? 'Скрыть пароль' : 'Показать пароль'}
                    >
                      {showPwNew ? <IconEyeOff /> : <IconEye />}
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor={pwNew2Id} className="text-[13px] text-[var(--text-muted)]">
                  Подтверждение нового пароля
                </label>
                <div className="relative mt-1">
                  <input
                    id={pwNew2Id}
                    type={showPwNew2 ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={pwdInputClass}
                    value={pwNew2}
                    onChange={(e) => setPwNew2(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-sm text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text)]"
                    onClick={() => setShowPwNew2((v) => !v)}
                    aria-label={showPwNew2 ? 'Скрыть пароль' : 'Показать пароль'}
                  >
                    {showPwNew2 ? <IconEyeOff /> : <IconEye />}
                  </button>
                </div>
              </div>
            </div>

            {pwErr ? (
              <EmbedMessageBlock
                className="mt-3 w-full"
                variant="error"
                size="field"
                role="alert"
                aria-live="assertive"
              >
                {pwErr}
              </EmbedMessageBlock>
            ) : null}

            <SmartCaptchaNotice
              serverEnabled={captcha.serverEnabled}
              challengeVisible={captcha.challengeVisible}
              status={captcha.status}
              onRetry={captcha.refresh}
            />

            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="secondary" disabled={pwSaving} onClick={() => setPwOpen(false)}>
                Отмена
              </Button>
              <Button
                type="button"
                disabled={pwSaving || captcha.loading}
                onClick={async () => {
                  if (!accessToken) return;
                  const cur = pwCurrent.trim();
                  const n1 = pwNew.trim();
                  const n2 = pwNew2.trim();
                  if (!cur || !n1 || !n2) {
                    setPwErr('Заполните все поля.');
                    return;
                  }
                  if (n1 !== n2) {
                    setPwErr('Новый пароль и подтверждение не совпадают.');
                    return;
                  }
                  const newPwErr = validateNewPassword(n1, { required: true });
                  if (newPwErr !== true) {
                    setPwErr(newPwErr);
                    return;
                  }
                  setPwErr(null);
                  setPwSaving(true);
                  try {
                    await captcha.run((captchaToken) =>
                      postChangeMePassword(
                        {
                          old_password: cur,
                          new_password: n1,
                          new_password_confirm: n2,
                          ...(captchaToken ? { captcha_token: captchaToken } : {})
                        },
                        accessToken
                      )
                    );
                    setPwOpen(false);
                    clearEmbedSession(orgSlug);
                    clearGlobalEmbedSession();
                    const next = new URLSearchParams(searchParams.toString());
                    next.set('password_changed', '1');
                    window.location.href = `/embed/sign-in?${next.toString()}`;
                  } catch (e) {
                    const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось сменить пароль';
                    setPwErr(msg);
                    if (captcha.enabled) captcha.reset();
                  } finally {
                    setPwSaving(false);
                  }
                }}
              >
                {pwSaving ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <LogoutConfirmDialog
        open={logoutOpen}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={() => {
          clearEmbedSession(orgSlug);
          clearGlobalEmbedSession();
          if (widgetContour === 'trainer' || widgetContour === 'trainer-mobile') {
            window.location.href = `/embed/trainer/sign-in${suffix}`;
          } else if (embedGlobal) {
            const p = new URLSearchParams();
            p.set('embed_global', '1');
            const parentOriginVal = searchParams.get('embed_parent_origin');
            if (parentOriginVal) p.set('embed_parent_origin', parentOriginVal);
            const wnVal = searchParams.get('wn');
            if (wnVal) p.set('wn', wnVal);
            const widgetChannelVal = searchParams.get('widget_channel');
            if (widgetChannelVal) p.set('widget_channel', widgetChannelVal);
            window.location.href = `/embed/?${p.toString()}`;
          } else {
            window.location.href = `/embed/sign-in${suffix}`;
          }
        }}
      />

      {me && accessToken ? (
        <DeleteAccountDialog
          open={deleteOpen}
          accessToken={accessToken}
          organizations={Array.from(
            new Set(
              (me.memberships ?? [])
                .map((m) => (m.organization_name || m.organization_slug || '').trim())
                .filter((n) => n !== '')
            )
          )}
          onDeleted={handleAccountDeleted}
          onCancel={() => setDeleteOpen(false)}
        />
      ) : null}

      {me && accessToken && leaveTarget ? (
        <LeaveOrganizationDialog
          open
          accessToken={accessToken}
          organizationId={leaveTarget.organization_id}
          organizationName={
            leaveTarget.organization_name ||
            leaveTarget.organization_slug ||
            'Текущая организация'
          }
          onLeft={() => {
            setLeaveTarget(null);
            void reloadMe();
          }}
          onCancel={() => setLeaveTarget(null)}
        />
      ) : null}
    </div>
    </div>
  );
}
